import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { 
    faExclamationTriangle, 
    faMoneyBillWave, 
    faUser, 
    faCalendar,
    faFilter,
    faDownload,
    faSearch,
    faChevronLeft,
    faChevronRight,
    faEdit,
    faTimes,
    faBan,
    faUndo,
    faListUl,
    faPaperclip,
    faSpinner,
    faSnowflake,
    faPercent
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import Api from '../../Api';
import SuccessModal from '../../components/SuccessModal';
import { useAuth } from '../../contexts/AuthContext';
import FrozenPayments from './FrozenPayments';
import RetenueGuarantees from './RetenueGuarantees';

const UnpaidPayments = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isRespAdmin = user?.role === 'resp_admin';
    // Retenues de garantie are F2F supervisors only: not for the CATI / control section heads
    const canSeeRetenues = !['resp_cati', 'resp_controle'].includes(user?.role);
    const canSeeReturns = ['resp_admin', 'administrateur_general', 'administrateur_general_superieur'].includes(user?.role);
    const [activeTab, setActiveTab] = useState('unpaid');
    const [expandedNotes, setExpandedNotes] = useState({}); // payment id -> full note shown
    // Caisse returns
    const [returns, setReturns] = useState([]);
    const [returnsLoading, setReturnsLoading] = useState(false);
    const [returnsPage, setReturnsPage] = useState(1);
    const [returnsTotalPages, setReturnsTotalPages] = useState(1);
    const [returnsTotalCount, setReturnsTotalCount] = useState(0);
    const RETURNS_PER_PAGE = 20;
    const [showReturnModal, setShowReturnModal] = useState(false);
    const showReturnModalDismiss = useModalDismiss(() => setShowReturnModal(false), { backdropClose: false, enabled: showReturnModal });
    const [returnAmount, setReturnAmount] = useState('');
    const [returnedTo, setReturnedTo] = useState('');
    const [returnNote, setReturnNote] = useState('');
    const [returnFiles, setReturnFiles] = useState([]);
    const [submittingReturn, setSubmittingReturn] = useState(false);
    const [returnError, setReturnError] = useState('');

    const [unpaidPayments, setUnpaidPayments] = useState([]);
    const [stats, setStats] = useState({});
    /** Aggregates from the list response so stats cards always match list count/total */
    const [listAggregates, setListAggregates] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchLoading, setSearchLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Modification modal
    const [showModifyModal, setShowModifyModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [modificationReason, setModificationReason] = useState('');
    const [modifying, setModifying] = useState(false);
    
    // Success modal
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    
    // Filters
    const [reasonFilter, setReasonFilter] = useState('');
    // Person-type filter: only for roles that see every section (section heads already see only theirs)
    const canFilterPersonType = !['resp_cati', 'chef_terrain', 'resp_controle'].includes(user?.role);
    const [personTypeFilter, setPersonTypeFilter] = useState('');
    const [surveyFilter, setSurveyFilter] = useState('');
    const [minAmount, setMinAmount] = useState('');
    const [maxAmount, setMaxAmount] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [surveys, setSurveys] = useState([]);
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [perPage] = useState(10);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500); // 500ms delay

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset to page 1 when debounced search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm]);

    useEffect(() => {
        fetchSurveys();
    }, []);

    useEffect(() => {
        if (activeTab === 'returns') fetchReturns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, returnsPage]);

    // Full-page spinner only on the first load: later refetches (filters, clearing them, paging) keep the
    // page mounted, otherwise clearing a filter blanked the whole page and remounted the frozen section.
    const hasLoadedOnceRef = useRef(false);

    useEffect(() => {
        const isSearch = hasLoadedOnceRef.current || debouncedSearchTerm !== '' || reasonFilter !== '' || surveyFilter !== '' || minAmount !== '' || maxAmount !== '' || dateFrom !== '' || dateTo !== '' || personTypeFilter !== '';
        hasLoadedOnceRef.current = true;
        fetchUnpaidPayments(isSearch);
        fetchStats();
    }, [currentPage, debouncedSearchTerm, reasonFilter, surveyFilter, minAmount, maxAmount, dateFrom, dateTo, personTypeFilter]);

    const fetchUnpaidPayments = async (isSearch = false) => {
        try {
            if (isSearch) {
                setSearchLoading(true);
            } else {
                setLoading(true);
            }
            
            const params = new URLSearchParams({
                page: currentPage,
                per_page: perPage,
            });

            if (debouncedSearchTerm) {
                params.append('search', debouncedSearchTerm);
            }
            if (reasonFilter) params.append('reason', reasonFilter);
            if (canFilterPersonType && personTypeFilter) params.append('person_type', personTypeFilter);
            if (surveyFilter) params.append('survey_id', surveyFilter);
            if (minAmount) params.append('min_amount', minAmount);
            if (maxAmount) params.append('max_amount', maxAmount);
            if (dateFrom) params.append('date_from', dateFrom);
            if (dateTo) params.append('date_to', dateTo);

            const response = await Api.get(`/payments/unpaid?${params}`);
            
            if (response.data.success) {
                const data = response.data.data;
                setUnpaidPayments(data.data || []);
                setTotalPages(data.last_page || 1);
                setTotalCount(data.total || 0);
                if (data.total != null) {
                    setListAggregates({
                        total_unpaid: data.total,
                        total_amount_unpaid: data.total_amount_unpaid ?? 0,
                        insufficient_amount: data.insufficient_amount ?? 0,
                        unpaid_confirmation: data.unpaid_confirmation ?? 0,
                    });
                }
            } else {
                setError('Erreur lors du chargement des paiements non effectués');
            }
        } catch (error) {
            console.error('Error fetching unpaid payments:', error);
            if (error.response?.data?.message) {
                setError(error.response.data.message);
            } else if (error.response?.status === 403) {
                setError('Accès non autorisé');
            } else if (error.response?.status === 401) {
                setError('Session expirée, veuillez vous reconnecter');
            } else {
                setError('Erreur lors du chargement des paiements non effectués');
            }
        } finally {
            setLoading(false);
            setSearchLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await Api.get('/payments/unpaid/stats');
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const fetchReturns = async () => {
        if (!canSeeReturns) return;
        try {
            setReturnsLoading(true);
            const response = await Api.get(`/payments/caisse/returns?page=${returnsPage}&per_page=${RETURNS_PER_PAGE}`);
            if (response.data.success) {
                setReturns(response.data.data || []);
                setReturnsTotalPages(response.data.pagination?.last_page || 1);
                setReturnsTotalCount(response.data.pagination?.total || 0);
            }
        } catch (error) {
            console.error('Error fetching returns:', error);
        } finally {
            setReturnsLoading(false);
        }
    };

    const openReturnModal = () => {
        setReturnAmount(''); setReturnedTo(''); setReturnNote(''); setReturnFiles([]); setReturnError('');
        setShowReturnModal(true);
    };

    const handleSubmitReturn = async () => {
        setReturnError('');
        const amount = parseFloat(returnAmount);
        const available = parseFloat(stats.caisse?.en_caisse ?? 0);
        if (!amount || amount <= 0) { setReturnError('Le montant doit être supérieur à 0.'); return; }
        if (amount > available + 0.001) { setReturnError(`Le montant dépasse le disponible en caisse (${available.toFixed(2)} MAD).`); return; }
        if (!returnedTo.trim()) { setReturnError('Le champ "Retourné à" est obligatoire.'); return; }
        try {
            setSubmittingReturn(true);
            const resp = await Api.post('/payments/caisse/return', { amount, returned_to: returnedTo.trim(), note: returnNote.trim() || null });
            if (!resp.data.success) { setReturnError(resp.data.message || 'Erreur lors de l\'enregistrement'); return; }
            const returnId = resp.data.return_id;
            // Upload attachments (optional)
            for (const file of returnFiles) {
                const fd = new FormData();
                fd.append('file', file);
                try { await Api.post(`/payments/caisse/returns/${returnId}/attachment`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); }
                catch (e) { console.error('attachment upload failed', e); }
            }
            setShowReturnModal(false);
            setSuccessMessage('Retour de caisse enregistré');
            setShowSuccessModal(true);
            await Promise.all([fetchStats(), fetchReturns()]);
        } catch (error) {
            setReturnError(error.response?.data?.message || 'Erreur lors de l\'enregistrement du retour');
        } finally {
            setSubmittingReturn(false);
        }
    };

    const downloadReturnAttachment = async (fileId, filename) => {
        try {
            const res = await Api.get(`/payments/proof-files/${fileId}/download`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url; link.setAttribute('download', filename || 'piece-jointe');
            document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
        } catch (e) { console.error('download error', e); }
    };

    const fetchSurveys = async () => {
        try {
            const response = await Api.get('/surveys?include_all=true&active_only=true');
            if (response.data.success && response.data.data) {
                setSurveys(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching surveys:', error);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const clearFilters = () => {
        setReasonFilter('');
        setMinAmount('');
        setMaxAmount('');
        setDateFrom('');
        setDateTo('');
        setSearchTerm('');
        setPersonTypeFilter('');
        setCurrentPage(1);
    };

    const getReasonText = (reason) => {
        switch (reason) {
            case 'deleted_survey_deduction':
                return 'Déduction pour enquêtes supprimées';
            case 'insufficient_amount':
                return 'Montant insuffisant';
            case 'unpaid_confirmation':
                return 'Non payé confirmé';
            case 'survey_closed':
                return 'Enquête fermée';
            case 'rejected':
                return 'Annulé';
            default:
                return reason;
        }
    };

    const getReasonColor = (reason) => {
        switch (reason) {
            case 'deleted_survey_deduction':
            case 'Déduction pour enquêtes supprimées':
                return 'bg-gray-100 text-gray-800';
            case 'insufficient_amount':
                return 'bg-orange-100 text-orange-800';
            case 'unpaid_confirmation':
                return 'bg-red-100 text-red-800';
            case 'survey_closed':
                return 'bg-blue-100 text-blue-800';
            case 'rejected':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    // Caisse-d'annulations state badge for a cancelled line (shown in the action column since
    // cancelled advances can't be modified).
    const renderReconBadge = (r) => {
        if (!r) return null;
        const remaining = parseFloat(r.remaining || 0);
        const returned = parseFloat(r.returned || 0);
        if (!r.reconciled || parseFloat(r.reconciled) <= 0) {
            return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">En caisse</span>;
        }
        // Return-based reconciliation (money returned out of the caisse)
        if (returned > 0 && (r.batches || []).length === 0) {
            if (remaining <= 0) {
                return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Réconcilié : Retour</span>;
            }
            return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700">En caisse {remaining.toFixed(2)} · Retourné {returned.toFixed(2)}</span>;
        }
        const lots = [...new Set((r.batches || []).map(b => b.batch_id))].map(id => `#${id}`).join(', ');
        const partial = remaining > 0 ? ' (partiel)' : '';
        if (r.has_finalized) {
            return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Réconcilié : Lot {lots}{partial}</span>;
        }
        return (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700" title="Réservé par un lot brouillon — sera réconcilié à la validation">
                Réservé : Lot {lots}{partial}
            </span>
        );
    };

    const handleModifyPayment = (payment) => {
        setSelectedPayment(payment);
        setNewStatus('');
        setModificationReason('');
        setShowModifyModal(true);
    };

    const handleModifySubmit = async () => {
        if (!newStatus || !modificationReason.trim()) {
            setSuccessMessage('Veuillez sélectionner un nouveau statut et fournir une justification');
            setShowSuccessModal(true);
            return;
        }

        setModifying(true);
        try {
            const response = await Api.post(`/payments/lines/${selectedPayment.payment_line_id}/modify-status`, {
                new_status: newStatus,
                reason: modificationReason.trim()
            });

            if (response.data.success) {
                setSuccessMessage('Statut modifié avec succès');
                setShowSuccessModal(true);
                setShowModifyModal(false);
                fetchUnpaidPayments(true);
                fetchStats();
            } else {
                setSuccessMessage(response.data.message || 'Erreur lors de la modification');
                setShowSuccessModal(true);
            }
        } catch (error) {
            console.error('Error modifying payment status:', error);
            setSuccessMessage(error.response?.data?.message || 'Erreur lors de la modification');
            setShowSuccessModal(true);
        } finally {
            setModifying(false);
        }
    };

    const closeModifyModal = () => {
        setShowModifyModal(false);
        setSelectedPayment(null);
        setNewStatus('');
        setModificationReason('');
    };

    // Helper function to get person info based on person_type
    const getPersonInfo = (payment) => {
        const personType = payment.person_type || 'interviewer'; // Default to interviewer for backward compatibility
        
        if (personType === 'interviewer') {
            return {
                name: payment.interviewer?.name || 'N/A',
                email: payment.interviewer?.email || 'N/A',
                ref: payment.interviewer?.ref || null,
                type: 'Enquêteur'
            };
        } else if (personType === 'controller') {
            return {
                name: payment.controller?.name || 'N/A',
                email: payment.controller?.email || 'N/A',
                ref: payment.controller?.ref || null,
                type: 'Contrôleur'
            };
        } else if (personType === 'supervisor') {
            return {
                name: payment.supervisor?.name || 'N/A',
                email: payment.supervisor?.email || 'N/A',
                ref: payment.supervisor?.ref || null,
                type: 'Prestataire'
            };
        }
        
        return {
            name: 'N/A',
            email: 'N/A',
            ref: null,
            type: 'Inconnu'
        };
    };


    if (loading && currentPage === 1) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">
                            Paiements Non Effectués
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Suivi des paiements non payés et des montants en attente
                        </p>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
                <button
                    onClick={() => setActiveTab('unpaid')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${activeTab === 'unpaid' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />Paiements non effectués
                </button>
                <button
                    onClick={() => setActiveTab('frozen')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${activeTab === 'frozen' ? 'border-sky-600 text-sky-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                    <FontAwesomeIcon icon={faSnowflake} className="mr-2" />Reports gelés
                </button>
                {canSeeRetenues && (
                    <button
                        onClick={() => setActiveTab('retenues')}
                        className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${activeTab === 'retenues' ? 'border-yellow-600 text-yellow-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                        <FontAwesomeIcon icon={faPercent} className="mr-2" />Retenues de garantie
                    </button>
                )}
                {canSeeReturns && (
                    <button
                        onClick={() => setActiveTab('returns')}
                        className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${activeTab === 'returns' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                        <FontAwesomeIcon icon={faListUl} className="mr-2" />Retours de caisse
                    </button>
                )}
            </div>

            {activeTab === 'frozen' && (
                <FrozenPayments navigate={navigate} />
            )}

            {activeTab === 'retenues' && canSeeRetenues && (
                <RetenueGuarantees navigate={navigate} />
            )}

            {activeTab === 'unpaid' && (
            <>
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                        <div className="p-2 bg-red-100 rounded-lg">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total</p>
                            <p className="text-2xl font-bold text-gray-900">{listAggregates ? listAggregates.total_unpaid : (stats.total_unpaid || 0)}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <FontAwesomeIcon icon={faMoneyBillWave} className="text-purple-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Montant En caisse</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {(parseFloat(stats.caisse?.en_caisse ?? 0) + parseFloat(stats.caisse?.reserve ?? 0)).toFixed(2)} MAD
                            </p>
                            {stats.caisse && parseFloat(stats.caisse.reserve) > 0 && (
                                <p className="text-xs text-gray-500 mt-1">Dont réservé : {parseFloat(stats.caisse.reserve).toFixed(2)}</p>
                            )}
                            {isRespAdmin && parseFloat(stats.caisse?.en_caisse ?? 0) > 0 && (
                                <button onClick={openReturnModal}
                                    className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-purple-600 hover:text-purple-800 border border-purple-200 rounded px-2 py-1 hover:bg-purple-50">
                                    <FontAwesomeIcon icon={faUndo} />Retour d'argent
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                        <div className="p-2 bg-red-100 rounded-lg">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Non Payé</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {(listAggregates?.unpaid_confirmation ?? stats.unpaid_confirmation) != null ? parseFloat(listAggregates?.unpaid_confirmation ?? stats.unpaid_confirmation).toFixed(2) : '0.00'} MAD
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center mb-4">
                    <FontAwesomeIcon icon={faFilter} className="text-gray-400 mr-2" />
                    <h2 className="text-lg font-semibold text-gray-900">Filtres</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Raison
                        </label>
                        <select
                            value={reasonFilter}
                            onChange={(e) => setReasonFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        >
                            <option value="">Toutes les raisons</option>
                            <option value="unpaid_confirmation">Non payé confirmé</option>
                            <option value="retenue">Retenue de garantie</option>
                            <option value="rejected">Annulé</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Enquête
                        </label>
                        <select
                            value={surveyFilter}
                            onChange={(e) => setSurveyFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        >
                            <option value="">Toutes les enquêtes</option>
                            {surveys.map((survey) => (
                                <option key={survey.id} value={survey.id}>
                                    {survey.title} {survey.limesurvey_id && `(${survey.limesurvey_id})`}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Montant Min (MAD)
                        </label>
                        <input
                            type="number"
                            value={minAmount}
                            onChange={(e) => setMinAmount(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                            placeholder="0"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Montant Max (MAD)
                        </label>
                        <input
                            type="number"
                            value={maxAmount}
                            onChange={(e) => setMaxAmount(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                            placeholder="1000"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date Début
                        </label>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date Fin
                        </label>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                    </div>

                    <div className="flex items-end space-x-2">
                        <button
                            onClick={clearFilters}
                            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
                        >
                            Effacer les filtres
                        </button>
                    </div>
                </div>

                {/* Search + person type */}
                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <FontAwesomeIcon
                            icon={faSearch}
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Rechercher par nom, REF, email ou notes..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                    </div>
                    {canFilterPersonType && (
                        <select
                            value={personTypeFilter}
                            onChange={(e) => { setPersonTypeFilter(e.target.value); setCurrentPage(1); }}
                            className="sm:w-56 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        >
                            <option value="">Tous les types</option>
                            <option value="supervisor">Prestataires F2F</option>
                            <option value="interviewer">Enquêteurs CATI</option>
                            <option value="controller">Contrôleurs</option>
                        </select>
                    )}
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mr-2" />
                        <span className="text-red-800">{error}</span>
                    </div>
                </div>
            )}

            {/* Unpaid Payments Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                        Paiements Non Effectués ({totalCount})
                    </h2>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Enquêteur
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Enquête(s)
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Montant
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Raison
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Notes
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {Array.isArray(unpaidPayments) && unpaidPayments.map((payment) => (
                                <tr key={payment.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <FontAwesomeIcon icon={faUser} className="text-gray-400 mr-2" />
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    {getPersonInfo(payment).name}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {getPersonInfo(payment).ref || 'N/A'}
                                                </div>
                                                <div className="text-xs text-gray-400 mt-1">
                                                    {getPersonInfo(payment).type}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {payment.surveys && Array.isArray(payment.surveys) && payment.surveys.length > 0 ? (
                                            <div className="flex flex-col gap-1">
                                                {payment.surveys.map((survey, idx) => (
                                                    <span 
                                                        key={survey.id || idx}
                                                        className="text-sm text-gray-700"
                                                    >
                                                        {survey.title} {survey.limesurvey_id && `(${survey.limesurvey_id})`}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-sm text-gray-400 italic">
                                                {payment.person_type === 'controller' ? 'N/A (Contrôleur)' : 'Non spécifiée'}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <FontAwesomeIcon icon={faMoneyBillWave} className="text-gray-400 mr-2" />
                                            <span className="text-sm font-medium text-gray-900">
                                                {payment.amount ? parseFloat(payment.amount).toFixed(2) : '0.00'} MAD
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${payment.is_retenue ? 'bg-white border border-yellow-400 text-yellow-700' : getReasonColor(payment.reason)}`}>
                                            {payment.reason_label || getReasonText(payment.reason)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        {/* Long notes: click to expand / collapse */}
                                        <div
                                            onClick={() => payment.notes && setExpandedNotes(prev => ({ ...prev, [payment.id]: !prev[payment.id] }))}
                                            title={payment.notes && !expandedNotes[payment.id] ? 'Cliquer pour voir la note complète' : undefined}
                                            className={`text-sm text-gray-900 max-w-xs ${payment.notes ? 'cursor-pointer' : ''} ${expandedNotes[payment.id] ? 'whitespace-pre-wrap break-words' : 'truncate'}`}
                                        >
                                            {payment.notes || '-'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <FontAwesomeIcon icon={faCalendar} className="text-gray-400 mr-2" />
                                            <span className="text-sm text-gray-900">
                                                {new Date(payment.created_at).toLocaleDateString('fr-FR')}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {payment.can_modify ? (
                                            <button
                                                onClick={() => handleModifyPayment(payment)}
                                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                            >
                                                Modifier
                                            </button>
                                        ) : (
                                            renderReconBadge(payment.reconciliation)
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {Array.isArray(unpaidPayments) && unpaidPayments.length === 0 && !loading && !searchLoading && (
                    <div className="text-center py-8">
                        <p className="text-gray-600">Aucun paiement non effectué trouvé</p>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {!loading && !searchLoading && totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4">
                    <div className="flex items-center text-sm text-gray-700">
                        <span>
                            Affichage de {((currentPage - 1) * 10) + 1} à {Math.min(currentPage * 10, totalCount)} sur {totalCount} paiements non effectués
                        </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                        </button>
                        
                        <div className="flex space-x-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                } else {
                                    pageNum = currentPage - 2 + i;
                                }
                                
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => handlePageChange(pageNum)}
                                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                            currentPage === pageNum
                                                ? 'bg-red-600 text-white'
                                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>
                        
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
            </>
            )}

            {/* Retours de caisse tab */}
            {activeTab === 'returns' && canSeeReturns && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                        <h2 className="text-lg font-bold text-gray-900">Retours de caisse</h2>
                        <span className="text-sm text-gray-500">{returns.length} retour(s)</span>
                    </div>
                    {returnsLoading ? (
                        <div className="p-8 text-center text-gray-500"><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />Chargement...</div>
                    ) : returns.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">Aucun retour de caisse.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Retourné à</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Par</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provenance</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pièces jointes</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {returns.map(r => (
                                        <tr key={r.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{r.returned_at}</td>
                                            <td className="px-4 py-3 text-sm font-semibold text-purple-700 whitespace-nowrap">{parseFloat(r.amount).toFixed(2)} MAD</td>
                                            <td className="px-4 py-3 text-sm text-gray-900">{r.returned_to}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{r.returned_by || '-'}</td>
                                            <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px]">{r.note || '-'}</td>
                                            <td className="px-4 py-3 text-xs text-gray-600">
                                                {(r.sources || []).map((s, i) => (
                                                    <div key={i}>
                                                        <span className="text-gray-900">{s.supervisor_name}</span> : {parseFloat(s.amount).toFixed(2)}
                                                        {s.batch_id && (
                                                            <span className="text-gray-400">
                                                                {' · Frais de déplacement '}
                                                                <button
                                                                    onClick={() => navigate(`/payments/${s.batch_id}`)}
                                                                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                                                    title="Ouvrir le lot de paiement"
                                                                >
                                                                    #{s.batch_id}
                                                                </button>
                                                                {s.survey_title ? ` : ${s.survey_title}` : ''}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                {(r.attachments || []).length === 0 ? <span className="text-gray-400">-</span> : (
                                                    <div className="flex flex-col gap-1">
                                                        {r.attachments.map(a => (
                                                            <button key={a.id} onClick={() => downloadReturnAttachment(a.id, a.original_filename)} className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs text-left">
                                                                <FontAwesomeIcon icon={faPaperclip} className="flex-shrink-0" /><span className="truncate max-w-[160px]">{a.original_filename}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Returns pagination (same design as the unpaid tab) */}
            {activeTab === 'returns' && !returnsLoading && returnsTotalPages > 1 && (
                <div className="mt-6 flex items-center justify-between bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4">
                    <div className="flex items-center text-sm text-gray-700">
                        <span>
                            Affichage de {((returnsPage - 1) * RETURNS_PER_PAGE) + 1} à {Math.min(returnsPage * RETURNS_PER_PAGE, returnsTotalCount)} sur {returnsTotalCount} retours
                        </span>
                    </div>

                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => setReturnsPage(returnsPage - 1)}
                            disabled={returnsPage === 1}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                        </button>

                        <div className="flex space-x-1">
                            {Array.from({ length: Math.min(5, returnsTotalPages) }, (_, i) => {
                                let pageNum;
                                if (returnsTotalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (returnsPage <= 3) {
                                    pageNum = i + 1;
                                } else if (returnsPage >= returnsTotalPages - 2) {
                                    pageNum = returnsTotalPages - 4 + i;
                                } else {
                                    pageNum = returnsPage - 2 + i;
                                }
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setReturnsPage(pageNum)}
                                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                            returnsPage === pageNum
                                                ? 'bg-red-600 text-white'
                                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            onClick={() => setReturnsPage(returnsPage + 1)}
                            disabled={returnsPage === returnsTotalPages}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Return money modal */}
            {showReturnModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900"><FontAwesomeIcon icon={faUndo} className="mr-2 text-purple-600" />Retour d'argent</h3>
                            <button onClick={() => setShowReturnModal(false)} className="text-gray-400 hover:text-gray-600"><FontAwesomeIcon icon={faTimes} className="w-5 h-5" /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-2 text-sm text-purple-700">
                                Disponible pour retour : <span className="font-bold">{parseFloat(stats.caisse?.en_caisse ?? 0).toFixed(2)} MAD</span>
                                <div className="text-xs text-purple-500 mt-0.5">Les montants réservés par un lot brouillon ne sont pas retournables.</div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Montant (MAD) *</label>
                                <input type="number" step="0.01" min="0" value={returnAmount} onChange={e => setReturnAmount(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="0.00" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Retourné à *</label>
                                <input type="text" value={returnedTo} onChange={e => setReturnedTo(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="Ex : Trésorerie, nom d'une personne…" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Note (optionnel)</label>
                                <textarea value={returnNote} onChange={e => setReturnNote(e.target.value)} rows="2"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="Notes…" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Pièces jointes (optionnel)</label>
                                <input type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.webp,.csv,.xls,.xlsx"
                                    onChange={e => setReturnFiles(Array.from(e.target.files))}
                                    className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200" />
                                {returnFiles.length > 0 && <div className="text-xs text-gray-500 mt-1">{returnFiles.length} fichier(s) sélectionné(s)</div>}
                            </div>
                            {returnError && <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{returnError}</div>}
                        </div>
                        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
                            <button onClick={() => setShowReturnModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Annuler</button>
                            <button onClick={handleSubmitReturn} disabled={submittingReturn}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center">
                                {submittingReturn ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />Enregistrement...</> : <>Confirmer le retour</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modification Modal */}
            {showModifyModal && selectedPayment && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Modifier le Statut du Paiement
                            </h3>
                            <button
                                onClick={closeModifyModal}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
                            </button>
                        </div>
                        
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {getPersonInfo(selectedPayment).type}
                                </label>
                                <p className="text-sm text-gray-900">
                                    {getPersonInfo(selectedPayment).name}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {getPersonInfo(selectedPayment).ref || 'N/A'}
                                </p>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Montant
                                </label>
                                <p className="text-sm text-gray-900">
                                    {selectedPayment.amount ? parseFloat(selectedPayment.amount).toFixed(2) : '0.00'} MAD
                                </p>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Nouveau Statut <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={newStatus}
                                    onChange={(e) => setNewStatus(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                >
                                    <option value="">Sélectionner un statut</option>
                                    <option value="unpaid">Non payé</option>
                                    <option value="insufficient">Montant insuffisant</option>
                                </select>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Justification <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={modificationReason}
                                    onChange={(e) => setModificationReason(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                    rows={3}
                                    placeholder="Expliquez pourquoi vous modifiez ce statut..."
                                />
                            </div>
                        </div>
                        
                        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                            <button
                                onClick={closeModifyModal}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleModifySubmit}
                                disabled={modifying || !newStatus || !modificationReason.trim()}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {modifying ? 'Modification...' : 'Modifier'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                title="Notification"
                message={successMessage}
                duration={3000}
            />
        </div>
    );
};

export default UnpaidPayments;
